import { CommonModule } from '@angular/common';
import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { NebularModule } from '../../../modules/nebular/nebular.module';
import * as moment from 'jalali-moment';
import {
  DatePickerComponent,
  IDatePickerConfig,
  NgxJDatePickerModule,
} from 'ngx-jdatepicker';
import { Moment } from 'jalali-moment';

@Component({
  standalone: true,
  selector: 'ngx-datepicker-field',
  templateUrl: './datepicker-field.component.html',
  styleUrls: ['./datepicker-field.component.scss'],
  imports: [CommonModule, FormsModule, NebularModule, NgxJDatePickerModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerFieldComponent),
      multi: true,
    },
  ],
})
export class DatePickerFieldComponent
  implements ControlValueAccessor, OnInit, OnDestroy
{
  @ViewChild(DatePickerComponent) private datePicker?: DatePickerComponent;

  @Input() label?: string;
  @Input() placeholder: string = '';
  @Input() control: any;
  @Input() disabled?: boolean = false;
  /**
   * Where to render the calendar popup. Pass `'body'` (or an element) when the field
   * sits inside an `overflow: hidden` container that would otherwise clip the popup.
   */
  @Input() appendTo?: string | HTMLElement;

  /** Internal value for display in picker (Moment for ngx-jdatepicker) */
  value: Moment | Moment[] | string | null = null;
  datePickerConfig: IDatePickerConfig = {
    disableKeypress: true,
    multipleYearsNavigateBy: 10,
  };

  private isTouchListenerActive = false;
  private readonly handleDocumentTouchStart = (event: TouchEvent): void => {
    const picker = this.datePicker;
    const target = event.target as Node | null;

    if (!picker?.areCalendarsShown || !target) {
      return;
    }

    const fieldElement = this.elementRef.nativeElement;
    const calendarElement = picker.calendarWrapper;
    if (
      fieldElement.contains(target) ||
      calendarElement?.contains(target)
    ) {
      return;
    }

    picker.api.close();
  };

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly elementRef: ElementRef<HTMLElement>,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  ngOnInit(): void {
    if (this.appendTo) {
      this.datePickerConfig = { ...this.datePickerConfig, appendTo: this.appendTo };
    }
  }

  ngOnDestroy(): void {
    this.removeTouchListener();
  }

  onPickerOpen(): void {
    if (this.isTouchListenerActive) {
      return;
    }

    this.document.addEventListener(
      'touchstart',
      this.handleDocumentTouchStart,
      { capture: true, passive: true },
    );
    this.isTouchListenerActive = true;
  }

  onPickerClose(): void {
    this.removeTouchListener();
  }

  // ---- ControlValueAccessor ----
  writeValue(value: string | Moment | null | undefined): void {
    if (value == null || value === '') {
      this.value = null;
      this.cdr.markForCheck();
      return;
    }
    let gMoment: Moment;
    if (moment.isMoment(value) && value.isValid()) {
      gMoment = value;
    } else if (typeof value === 'string') {
      // Support ISO (with or without time) and date-only YYYY-MM-DD
      const trimmed = value.trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        gMoment = moment(trimmed, 'YYYY-MM-DD', true);
      } else {
        gMoment = moment(trimmed);
      }
    } else {
      this.value = null;
      this.cdr.markForCheck();
      return;
    }
    if (!gMoment.isValid()) {
      this.value = null;
      this.cdr.markForCheck();
      return;
    }
    // Jalali moment for ngx-jdatepicker (fa locale)
    this.value = moment.from(
      gMoment.format('jYYYY-jMM-jDD'),
      'fa',
      'YYYY-MM-DD',
    );
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  /** Called when user selects a date (picker may emit Moment or Moment[]) */
  onInput(selected: Moment | Moment[]): void {
    const single = Array.isArray(selected) ? selected[0] : selected;
    this.value = single ?? null;

    const isoValue = single?.isValid() === true ? single.toISOString() : null;
    this.onChange(isoValue);
    this.onTouched();
  }

  onChange = (_: any) => {};
  onTouched = () => {};

  private removeTouchListener(): void {
    if (!this.isTouchListenerActive) {
      return;
    }

    this.document.removeEventListener(
      'touchstart',
      this.handleDocumentTouchStart,
      true,
    );
    this.isTouchListenerActive = false;
  }
}
